import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useProductAuditLogs } from '@/api/audit-logs'
import { useIdempotencyKey } from '@/api/idempotency'
import { useArchiveProduct, useProduct, useUnarchiveProduct } from '@/api/products'
import type { Product } from '@/api/products'
import { useWorkspace } from '@/api/workspace'
import { BomTab } from '@/components/boms/bom-tab'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { ModelsTab } from '@/components/models/models-tab'
import { ProductHub } from '@/components/product-setup/product-hub'
import { setupSearch } from '@/components/product-setup/setup-utils'
import { SetupWizard } from '@/components/product-setup/setup-wizard'
import { productErrorMessage } from '@/components/products/error-message'
import { ProductBadges } from '@/components/products/product-badges'
import { RecordHistory } from '@/components/record-history/record-history'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { VariantsTab } from '@/components/variants/variants-tab'
import { CogsSummaryTab } from '@/components/cogs/cogs-summary-tab'
import { formatBps } from '@/components/cogs/percent'

// Product detail (UI-SCREENS P-03, #99): the header, Edit / Archive / Un-archive for the Owner,
// and the Models (#100, with each Model's Yield and top failure reasons, 04j #136), BOM (#103) and
// Variants (#102) tabs, and the COGS summary (08c #204). Staff see it read only; the Owner also
// sees the History section (#105).
const TABS = ['models', 'bom', 'variants', 'cogs'] as const

// ?tab=variants opens that tab, e.g. when P-09 returns here after a save (#102).
// With the `product_setup` feature flag on, the Product hub instead, and with ?setup=<step> the
// setup wizard at that step.
const searchSchema = z.object({ tab: z.enum(TABS).optional().catch(undefined), setup: setupSearch })

export const Route = createFileRoute('/w/$slug/products/$productId/')({
  component: ProductDetail,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'products.detail.title' },
})

function ProductDetail() {
  const { slug, productId } = Route.useParams()
  const { setup } = Route.useSearch()
  const workspace = useWorkspace(slug)

  if (workspace.isPending) return <LoadingSkeleton variant="page" />
  if (workspace.data?.features.product_setup) {
    return setup ? (
      <SetupWizard key={setup} slug={slug} step={setup} productId={Number(productId)} />
    ) : (
      <ProductHub slug={slug} productId={Number(productId)} />
    )
  }
  return <ClassicProductDetail />
}

function ClassicProductDetail() {
  const { t } = useTranslation()
  const { slug, productId } = Route.useParams()
  const { tab = 'models' } = Route.useSearch()
  const product = useProduct(slug, Number(productId))
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useProductAuditLogs(slug, Number(productId), { enabled: isOwner })

  if (product.isPending) return <LoadingSkeleton variant="page" />
  if (product.isError || !product.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <Link to="/w/$slug/products" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('products.back')}
      </Link>
      <div className="flex flex-col gap-2">
        <p className="font-mono text-sm text-muted-foreground">{product.data.code}</p>
        <h1 className="font-heading text-xl font-semibold">{product.data.name}</h1>
        <ProductBadges product={product.data} />
      </div>
      <Actions slug={slug} product={product.data} />
      <CogsInputs product={product.data} isOwner={isOwner} />
      <Tabs defaultValue={tab}>
        <TabsList>
          {TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {t(`products.detail.tabs.${tab}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="models">
          <ModelsTab slug={slug} product={product.data} />
        </TabsContent>
        <TabsContent value="bom">
          <BomTab slug={slug} product={product.data} />
        </TabsContent>
        <TabsContent value="variants">
          <VariantsTab slug={slug} product={product.data} />
        </TabsContent>
        <TabsContent value="cogs">
          <CogsSummaryTab slug={slug} productId={product.data.id} />
        </TabsContent>
      </Tabs>
      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// The 08 COGS overrides (08a #202). The target margin only derives the Suggested Vendor Price, a
// revenue figure, so Staff never see it (#202 Q4); the default labor minutes are a cost input.
function CogsInputs({ product, isOwner }: { product: Product; isOwner: boolean }) {
  const { t } = useTranslation()
  const minutes = product.default_labor_minutes_per_unit

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
      {isOwner ? (
        <div className="contents">
          <dt className="text-sm text-muted-foreground">{t('products.detail.cogs.targetMargin')}</dt>
          <dd className="font-medium">
            {product.target_margin_bps === null || product.target_margin_bps === undefined ? (
              <span className="text-sm font-normal text-muted-foreground">
                {t('products.detail.cogs.targetMarginDefault')}
              </span>
            ) : (
              formatBps(product.target_margin_bps)
            )}
          </dd>
        </div>
      ) : null}
      <div className="contents">
        <dt className="text-sm text-muted-foreground">{t('products.detail.cogs.defaultLaborMinutes')}</dt>
        <dd className="font-medium">{minutes === null ? '—' : minutes}</dd>
      </div>
      <p className="text-sm text-muted-foreground sm:col-span-2">{t('products.detail.cogs.defaultLaborMinutesHint')}</p>
    </dl>
  )
}

function Actions({ slug, product }: { slug: string; product: Product }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const archive = useArchiveProduct()
  const unarchive = useUnarchiveProduct()
  const archiveKey = useIdempotencyKey()
  const unarchiveKey = useIdempotencyKey()
  const [confirming, setConfirming] = useState<'archive' | 'unarchive' | null>(null)
  const [notice, setNotice] = useState('')
  const name = product.name
  const busy = archive.isPending || unarchive.isPending
  const error = archive.isError ? archive.error : unarchive.isError ? unarchive.error : null

  function run(action: 'archive' | 'unarchive') {
    setConfirming(null)
    const key = action === 'archive' ? archiveKey : unarchiveKey
    const mutation = action === 'archive' ? archive : unarchive
    mutation.mutate(
      { params: { path: { slug, product_id: product.id }, header: key.headerFor({ id: product.id }) } },
      {
        onSuccess: () => setNotice(t(`products.archive.${action}d`, { name })),
        onSettled: (_data, mutationError) => key.settle(mutationError),
      },
    )
  }

  if (!product.can.update && !product.can.archive && !product.can.unarchive) return null

  return (
    <div className="flex flex-col gap-3">
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {notice}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {error ? productErrorMessage(t, error) : ''}
      </p>
      <div className="flex flex-wrap gap-2">
        {product.can.update ? (
          <Button
            variant="outline"
            className="h-11"
            onClick={() =>
              navigate({ to: '/w/$slug/products/$productId/edit', params: { slug, productId: String(product.id) } })
            }
          >
            {t('products.edit')}
          </Button>
        ) : null}
        {product.can.archive ? (
          <Button variant="ghost" className="h-11 text-destructive" disabled={busy} onClick={() => setConfirming('archive')}>
            {t('products.archive.archive')}
          </Button>
        ) : null}
        {product.can.unarchive ? (
          <Button variant="outline" className="h-11" disabled={busy} onClick={() => setConfirming('unarchive')}>
            {t('products.archive.unarchive')}
          </Button>
        ) : null}
      </div>

      {confirming ? (
        <AlertDialog open onOpenChange={(open) => (open ? undefined : setConfirming(null))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(confirming === 'archive' ? 'products.archive.confirmTitle' : 'products.archive.confirmUnarchiveTitle', {
                  name,
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(confirming === 'archive' ? 'products.archive.confirmBody' : 'products.archive.confirmUnarchiveBody')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11">{t('products.archive.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="h-11" onClick={() => run(confirming)}>
                {t('products.archive.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  )
}
